import { PreviewOverlay } from "components/PreviewOverlay"
import { TintViewer } from "components/TintViewer"
import React, { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ProductV2 } from "types"
import {
  Box,
  IconButton,
  SingleSelect,
  SingleSelectOption,
  Typography,
} from "@strapi/design-system"
import { Expand } from "@strapi/icons"
import { fractionalRgbToHex, hexToRgbString } from "../ColorItem/helpers"
import { MODEL_IMAGES } from "../PreviewOverlay/types"
import { ProductCategories } from "components/Navbar/types"

interface PreviewProps {
  productItem: ProductV2
  type: ProductCategories
  size?: number
  disabled?: boolean
  setActiveColorIndex?: (ind: number) => void
  activeColorIndex?: number
}

const TINT_VIEWER_SIZE = 230

export const Preview: FC<PreviewProps> = ({
  productItem,
  type = ProductCategories.MAKEUP,
  size = TINT_VIEWER_SIZE,
  disabled,
  setActiveColorIndex,
  activeColorIndex,
}) => {
  const { t } = useTranslation()
  const [previewOverlay, setPreviewOverlay] = useState(false)
  const [image, setImage] = useState(MODEL_IMAGES[0])
  const [color, setColor] = useState<string>()

  useEffect(() => {
    if (type === ProductCategories.MAKEUP && productItem?.colors[activeColorIndex])
      setColor(productItem.colors[activeColorIndex].color)
  }, [productItem, activeColorIndex, type])

  return (
    <div>
      {previewOverlay && (
        <PreviewOverlay
          effectType={type}
          productItem={productItem}
          image={image}
          onChangeImage={setImage}
          activeColorIndex={activeColorIndex}
          setActiveColorIndex={setActiveColorIndex}
          onClose={() => setPreviewOverlay(false)}
        />
      )}
      <Box className="p-2 flex flex-col gap-2">
        <Typography variant="omega" textColor="neutral600">
          {t("previewVTO.virtualTryOnPreview")}
        </Typography>
        <Box className="relative flex flex-col items-center justify-center">
          {previewOverlay || disabled ? (
            <Box
              background="neutral200"
              className="flex items-center justify-center m-0 p-0"
              style={{
                width: size,
                height: size,
                maxWidth: size,
                minWidth: size,
                maxHeight: size,
                minHeight: size,
              }}
            >
              {disabled && (
                <Box className="flex items-center justify-center px-5 text-center flex-row flex-wrap gap-[4px]">
                  <Typography className="text-[#666684] font-normal" variant="pi">
                    {t("previewVTO.fillVirtualTryOnInfo")}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <>
              <div className="absolute top-1 left-1 z-50">
                <IconButton
                  className=""
                  onClick={() => setPreviewOverlay(true)}
                  aria-label={t("previewVTO.expand")}
                  icon={<Expand />}
                />
              </div>
              <TintViewer
                productItem={productItem}
                activeColor={color}
                image={image}
                size={size}
                effectType={type}
              />
            </>
          )}
        </Box>
        <Box />
        {productItem?.colors?.length && type === ProductCategories.MAKEUP ? (
          <SingleSelect
            required
            onChange={(value: number) => setActiveColorIndex?.(value)}
            value={activeColorIndex}
          >
            {productItem.colors.map((to, idx) => (
              <SingleSelectOption key={idx} value={idx}>
                <Box className="flex items-center gap-4">
                  <Box
                    className="rounded"
                    style={{
                      width: 30,
                      height: 30,
                      backgroundColor: fractionalRgbToHex(
                        to.color[0] === "#" ? hexToRgbString(to.color) : to.color
                      ),
                    }}
                  />
                  <Typography>{t("productPreview.tone", { color: to.color })}</Typography>
                </Box>
              </SingleSelectOption>
            ))}
          </SingleSelect>
        ) : null}
      </Box>
    </div>
  )
}
